import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check } from 'lucide-react';
import { sendPasswordResetCode, verifyPasswordResetCode, resetPassword } from '../api/auth';
import styles from './ResetPasswordPage.module.css';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import Logo from '../components/Logo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TIMER_SECONDS = 300;

function formatTime(s) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function validatePassword(pw) {
  if (pw.length < 8 || pw.length > 20) return '8~20자로 입력해주세요';
  if (/\s/.test(pw)) return '공백은 사용할 수 없어요';
  if (!/[A-Za-z]/.test(pw)) return '영문을 포함해야 해요';
  if (!/[0-9]/.test(pw)) return '숫자를 포함해야 해요';
  if (!/[^A-Za-z0-9\s]/.test(pw)) return '특수문자를 포함해야 해요';
  return null;
}

function EyeIcon({ open }) {
  const Comp = open ? Eye : EyeOff;
  return <Comp size={20} strokeWidth={1.8} />;
}

function SuccessScreen({ onLogin }) {
  return (
    <div className={styles.successPage}>
      <div className={styles.successContent}>
        <div className={styles.checkCircle}>
          <Check size={32} strokeWidth={2.5} />
        </div>
        <h2 className={styles.successTitle}>비밀번호 변경 완료</h2>
        <p className={styles.successSub}>
          비밀번호가 성공적으로 변경되었어요<br />
          새 비밀번호로 로그인해주세요
        </p>
      </div>
      <button className={styles.submitBtn} onClick={onLogin}>
        로그인 하러 가기
      </button>
    </div>
  );
}

export default function ResetPasswordPage() {
  useDocumentMeta({ title: '비밀번호 재설정', noIndex: true });
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [timer, setTimer] = useState(0);
  const [codeError, setCodeError] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const timerRef = useRef(null);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const startTimer = () => {
    clearInterval(timerRef.current);
    setTimer(TIMER_SECONDS);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const emailValid = EMAIL_RE.test(email.trim());
  const emailError = emailTouched && email.length > 0 && !emailValid;

  const handleSendCode = async () => {
    if (!emailValid || isSending || codeVerified) return;
    setIsSending(true);
    setCodeError('');
    setCode('');
    setCodeVerified(false);
    setVerificationToken('');
    try {
      await sendPasswordResetCode(email.trim());
      setCodeSent(true);
      startTimer();
    } catch (e) {
      setCodeError(e?.response?.data?.message ?? '인증번호 전송에 실패했어요. 다시 시도해주세요.');
    } finally {
      setIsSending(false);
    }
  };

  const verifyCode = async (digits) => {
    if (isVerifying || codeVerified) return;
    setIsVerifying(true);
    setCodeError('');
    try {
      const token = await verifyPasswordResetCode(email.trim(), digits);
      setVerificationToken(token);
      setCodeVerified(true);
      clearInterval(timerRef.current);
    } catch (e) {
      setCodeError(e?.response?.data?.message ?? '인증번호가 올바르지 않습니다');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    setCodeError('');
    if (digits.length === 6 && !codeVerified) verifyCode(digits);
  };

  const pwError = newPassword.length > 0 ? validatePassword(newPassword) : null;
  const confirmError =
    confirmTouched && confirmPassword.length > 0 && newPassword !== confirmPassword
      ? '비밀번호가 일치하지 않아요'
      : null;
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canReset = codeVerified && !validatePassword(newPassword) && passwordsMatch;

  const handleReset = async (e) => {
    e?.preventDefault();
    if (!canReset || isResetting) return;
    setIsResetting(true);
    setResetError('');
    try {
      await resetPassword(email.trim(), verificationToken, newPassword);
      setIsSuccess(true);
    } catch (e) {
      setResetError(e?.response?.data?.message ?? '비밀번호 변경에 실패했어요. 다시 시도해주세요.');
    } finally {
      setIsResetting(false);
    }
  };

  if (isSuccess) {
    return <SuccessScreen onLogin={() => navigate('/login')} />;
  }

  return (
    <div className={styles.page}>      <div className={styles.body}>
        <Link className={styles.logo} to="/"><Logo height={26} /></Link>
        <h1 className={styles.heading}>계정찾기</h1>
        <p className={styles.desc}>
          가입 시 사용한 이메일 주소를 입력하면<br />
          인증번호를 보내드릴게요
        </p>

        <form className={styles.form} onSubmit={handleReset} noValidate>
          {/* 이메일 */}
          <div className={styles.field}>
            <label htmlFor="reset-email" className={styles.label}>이메일</label>
            <input
                id="reset-email"
              type="email"
              className={`${styles.input} ${emailError ? styles.inputError : ''}`}
              placeholder="이메일을 입력해주세요"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (codeSent) {
                  setCodeSent(false);
                  setCodeVerified(false);
                  setCode('');
                  setCodeError('');
                  clearInterval(timerRef.current);
                  setTimer(0);
                }
              }}
              onBlur={() => setEmailTouched(true)}
              disabled={codeVerified}
              autoComplete="email"
            />
            {emailError && <p className={styles.fieldError}>이메일 형식을 확인해주세요</p>}
          </div>

          {/* 인증번호 */}
          <div className={styles.field}>
            <label htmlFor="reset-code" className={styles.label}>인증번호</label>
            <div className={styles.codeRow}>
              <input id="reset-code"
                type="text"
                inputMode="numeric"
                className={`${styles.input} ${styles.codeInput} ${codeError ? styles.inputError : ''}`}
                placeholder="인증번호 6자리"
                value={code}
                onChange={handleCodeChange}
                disabled={!codeSent || codeVerified}
                maxLength={6}
              />
              {codeSent && !codeVerified && timer > 0 && (
                <span className={`${styles.timer} ${timer < 60 ? styles.timerRed : ''}`}>
                  {formatTime(timer)}
                </span>
              )}
              <button
                type="button"
                className={`${styles.codeBtn} ${!emailValid || isSending || codeVerified ? styles.codeBtnDisabled : ''}`}
                onClick={handleSendCode}
                disabled={!emailValid || isSending || codeVerified}
              >
                {isSending ? '전송 중…' : codeSent ? '재전송' : '전송'}
              </button>
            </div>
            {codeVerified && <p className={styles.verifiedMsg}><Check size={13} strokeWidth={3} /> 인증이 완료되었습니다</p>}
            {codeError && <p className={styles.fieldError}>{codeError}</p>}
          </div>

          {/* 인증 후에만 비밀번호 필드 표시 */}
          {codeVerified && (
            <>
              {/* 새 비밀번호 */}
              <div className={styles.field}>
                <label htmlFor="reset-new-password" className={styles.label}>새 비밀번호</label>
                <p className={styles.hint}>8~20자, 영문·숫자·특수문자 포함, 공백 불가</p>
                <div className={styles.pwWrap}>
                  <input id="reset-new-password"
                    type={showNewPw ? 'text' : 'password'}
                    className={`${styles.input} ${styles.pwInput} ${pwError ? styles.inputError : ''}`}
                    placeholder="비밀번호를 입력해주세요"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button type="button" className={styles.eyeBtn} onClick={() => setShowNewPw((p) => !p)}>
                    <EyeIcon open={showNewPw} />
                  </button>
                </div>
                {pwError && <p className={styles.fieldError}>{pwError}</p>}
              </div>

              {/* 새 비밀번호 확인 */}
              <div className={styles.field}>
                <label htmlFor="reset-new-password-confirm" className={styles.label}>새 비밀번호 확인</label>
                <div className={styles.pwWrap}>
                  <input id="reset-new-password-confirm"
                    type={showConfirmPw ? 'text' : 'password'}
                    className={`${styles.input} ${styles.pwInput} ${confirmError ? styles.inputError : ''}`}
                    placeholder="비밀번호를 다시 입력해주세요"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => setConfirmTouched(true)}
                    autoComplete="new-password"
                  />
                  <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirmPw((p) => !p)}>
                    <EyeIcon open={showConfirmPw} />
                  </button>
                </div>
                {confirmError && <p className={styles.fieldError}>{confirmError}</p>}
                {passwordsMatch && !confirmError && (
                  <p className={styles.verifiedMsg}><Check size={13} strokeWidth={3} /> 비밀번호가 일치합니다</p>
                )}
              </div>
            </>
          )}

          {resetError && <p className={styles.submitError} role="alert">{resetError}</p>}

          <button
            type="submit"
            className={`${styles.submitBtn} ${!canReset ? styles.submitBtnDisabled : ''}`}
            disabled={!canReset || isResetting}
          >
            {isResetting ? '변경 중…' : '비밀번호 변경'}
          </button>

          <Link to="/" className={styles.exitLink}>홈으로 돌아가기</Link>
        </form>
      </div>
    </div>
  );
}
